import React from 'react';
import {
  Trophy,
  ChevronUp,
  ChevronDown,
  Edit,
  Trash2,
  Plus
} from 'lucide-react';
import { Card, CardContent } from '../../../components/ui/Card';
import Button from '../../../components/ui/Button';
import type { Achievement } from '../types';

interface AchievementsSectionProps {
  achievements: Achievement[];
  newAchievement: Achievement;
  editingIndex: number | null;
  onNewAchievementChange: (achievement: Achievement) => void;
  onAddAchievement: () => void;
  onEditAchievement: (index: number) => void;
  onUpdateAchievement: (index: number, field: keyof Achievement, value: string) => void;
  onSaveAchievementEdit: (index: number) => void;
  onDeleteAchievement: (index: number) => void;
  onMoveAchievement: (index: number, direction: 'up' | 'down') => void;
  t: (key: string) => string;
}

const AchievementsSection: React.FC<AchievementsSectionProps> = ({
  achievements,
  newAchievement,
  editingIndex,
  onNewAchievementChange,
  onAddAchievement,
  onEditAchievement,
  onUpdateAchievement,
  onSaveAchievementEdit,
  onDeleteAchievement,
  onMoveAchievement,
  t
}) => {
  return (
    <Card>
      <CardContent className="p-6">
        <h2 className="text-lg font-semibold text-foreground mb-4 flex items-center">
          <Trophy className="w-5 h-5 me-2" />
          {t('Achievements')}
        </h2>

        {/* Existing Achievements */}
        <div className="space-y-3 mb-4">
          {achievements?.map((achievement, index) => (
            <div key={achievement.id || index} className="border border-border rounded-lg p-4">
              {editingIndex === index ? (
                <div className="space-y-3">
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-2">{t('Title')}</label>
                    <input
                      type="text"
                      value={achievement.title}
                      onChange={(e) => onUpdateAchievement(index, 'title', e.target.value)}
                      className="w-full px-3 py-2 border border-input rounded-lg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/20 focus-visible:border-primary"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-2">{t('Date')}</label>
                    <input
                      type="text"
                      value={achievement.date}
                      onChange={(e) => onUpdateAchievement(index, 'date', e.target.value)}
                      className="w-full px-3 py-2 border border-input rounded-lg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/20 focus-visible:border-primary"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-2">{t('Description (Optional)')}</label>
                    <textarea
                      value={achievement.description}
                      onChange={(e) => onUpdateAchievement(index, 'description', e.target.value)}
                      rows={2}
                      className="w-full px-3 py-2 border border-input rounded-lg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/20 focus-visible:border-primary"
                      placeholder={t('Brief description of the achievement')}
                    />
                  </div>
                  <Button
                    size="sm"
                    onClick={() => onSaveAchievementEdit(index)}
                  >
                    {t('Save')}
                  </Button>
                </div>
              ) : (
                <div>
                  <div className="flex items-start justify-between mb-2">
                    <div className="flex-1">
                      <h3 className="font-semibold text-foreground">{achievement.title}</h3>
                      <p className="text-sm text-muted-foreground">{achievement.date}</p>
                      {achievement.description && (
                        <p className="text-sm text-muted-foreground mt-1">{achievement.description}</p>
                      )}
                    </div>
                    <div className="flex items-center gap-1 ms-2">
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => onMoveAchievement(index, 'up')}
                        disabled={index === 0}
                        className="h-8 w-8"
                      >
                        <ChevronUp className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => onMoveAchievement(index, 'down')}
                        disabled={index === achievements.length - 1}
                        className="h-8 w-8"
                      >
                        <ChevronDown className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => onEditAchievement(index)}
                        className="h-8 w-8"
                      >
                        <Edit className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => onDeleteAchievement(index)}
                        className="h-8 w-8 hover:bg-destructive/10 text-destructive"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Add New Achievement */}
        <div className="border-t border-border pt-4">
          <h3 className="text-sm font-semibold text-foreground mb-3">{t('Add New Achievement')}</h3>
          <div className="space-y-3">
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">{t('Title')}</label>
              <input
                type="text"
                value={newAchievement.title}
                onChange={(e) => onNewAchievementChange({ ...newAchievement, title: e.target.value })}
                placeholder={t('e.g., Employee of the Month')}
                className="w-full px-3 py-2 border border-input rounded-lg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/20 focus-visible:border-primary"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">{t('Date')}</label>
              <input
                type="text"
                value={newAchievement.date}
                onChange={(e) => onNewAchievementChange({ ...newAchievement, date: e.target.value })}
                placeholder={t('e.g., Q4 2023')}
                className="w-full px-3 py-2 border border-input rounded-lg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/20 focus-visible:border-primary"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">{t('Description (Optional)')}</label>
              <textarea
                value={newAchievement.description}
                onChange={(e) => onNewAchievementChange({ ...newAchievement, description: e.target.value })}
                rows={2}
                className="w-full px-3 py-2 border border-input rounded-lg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/20 focus-visible:border-primary"
                placeholder={t('Brief description of the achievement')}
              />
            </div>
            <Button
              variant="outline"
              onClick={onAddAchievement}
              disabled={!newAchievement.title || !newAchievement.date}
              className="gap-2"
            >
              <Plus className="w-4 h-4" />
              {t('Add Achievement')}
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default AchievementsSection;
